import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/**
 * Search box: an icon field with a magnifying glass. `value` follows every
 * keystroke; `search` emits the text once typing has paused for `debounce`
 * milliseconds (immediately when `debounce` is 0, the default).
 *
 *   <sh-search-input [(value)]="text" [debounce]="300" (search)="load($event)" />
 */
@Component({
  selector: 'sh-search-input',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShSearchInputComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-iconfield>
      <p-inputicon styleClass="fa-solid fa-magnifying-glass"></p-inputicon>
      <input
        pInputText
        type="text"
        [ngModel]="value()"
        (ngModelChange)="onInput($event)"
        [ngModelOptions]="{ standalone: true }"
        [attr.id]="controlId()"
        [attr.placeholder]="placeholder() | translate"
        [attr.aria-label]="ariaLabel() | translate"
        [readOnly]="isReadonly()"
        [disabled]="isDisabled()"
        [pSize]="pSize()"
        [fluid]="true"
        (blur)="markTouched()"
      />
    </p-iconfield>
  `,
})
export class ShSearchInputComponent
  extends ShFormControl<string>
  implements OnDestroy
{
  /** Milliseconds of silence before `search` emits. */
  readonly debounce = input(0);
  // eslint-disable-next-line @angular-eslint/no-output-native -- public output name; renaming would change the API
  readonly search = output<string>();

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );

  private _timer?: ReturnType<typeof setTimeout>;

  protected onInput(text: string | null): void {
    this.commit(text);
    if (this._timer) clearTimeout(this._timer);
    const wait = this.debounce();
    if (wait > 0) {
      this._timer = setTimeout(() => this.search.emit(text ?? ''), wait);
    } else {
      this.search.emit(text ?? '');
    }
  }

  ngOnDestroy(): void {
    if (this._timer) clearTimeout(this._timer);
  }
}
